import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { CustomToasterService, ProfileService } from '@app-utils';
import { Router } from '@angular/router';

const IGNORE_401 = [
  '/api/v1/custom-fields/list',
  '/api/v1/fields/list',
  '/api/v1/master-settings',
];

const SILENT_ERROR_URLS = [
  '/api/v1/master-settings/module-field-default',
  '/import/upload',
  '/import/status/',
  // Sidebar / inbox badge poll — best-effort; must not toast on Pricing and other modules.
  '/api/v1/conversations/my/head',
  // Sidebar AI usage meter poll — best-effort; the meter hides itself when it fails.
  '/api/v1/ai/assistant/usage',
];

const SILENT_404_URLS = ['/api/v1/settings/subscription'];

// Best-effort refreshes that retry on their own: a failure is not worth a toast.
const SILENT_ERROR_PATTERNS = [
  /api\/social\/connections\/[^/]+\/sync/,
  // The Pages panel explains a failed listing itself.
  /api\/social\/connections\/[^/]+\/pages$/,
];

/**
 * Error Interceptor
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly _router = inject(Router);
  private readonly _profile = inject(ProfileService);
  private readonly _customToaster = inject(CustomToasterService);

  /**
   *
   * @param request
   * @param next
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        const error: string =
          typeof err?.error === 'string' ? err?.error : err?.error?.message;
        if (err.status === 401) {
          const ignore = IGNORE_401.some((itm) => request.url.includes(itm));
          this._customToaster.error(
            error || 'Invalid user name or password',
            'Error',
          );
          if (!ignore) {
            this._profile.logout();
            void this._router.navigate(['/auth']);
          }
          return throwError(() => err);
        }
        // 403: authenticated but not permitted — toast only (no logout / redirect).
        if (!request.url.includes('/image')) {
          const silent =
            SILENT_ERROR_URLS.some((url) => request.url.includes(url)) ||
            SILENT_ERROR_PATTERNS.some((re) => re.test(request.url)) ||
            (err.status === 404 &&
              SILENT_404_URLS.some((url) => request.url.includes(url)));
          if (!silent) {
            this._customToaster.error(error, 'Error');
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
